import { useDisplayCurrency } from './currency/DisplayCurrencyProvider';
import { displayTotal, moneyPrefix as moneyPrefixFor } from './Util/formatUtils';
import React, { useEffect, useRef, useState } from 'react';
import { useBrandTokenStyle } from '@charitystack/design-tokens/provider';
import { useBrandColors, safePostMessageToParent } from '@charitystack/shared';
import { getEnvironment, getBackendUrl } from './config/env';
import NonFormSkeleton from './Util/NonFormSkeleton';
import { fetchElementsConfigOnce, readSharedJson } from './Util/elementsConfigCache';

const GOAL_ABBREVIATE_THRESHOLD = 10_000;

function abbreviateNumber(number) {
  if (typeof number !== 'number') {
    throw new Error('Input must be a number.');
  }
  if (number >= 1_000_000_000) {
    const val = number / 1_000_000_000;
    return (val % 1 === 0 ? val.toFixed(0) : val.toFixed(1).replace(/\.0$/, '')) + 'B';
  }
  if (number >= 1_000_000) {
    const val = number / 1_000_000;
    return (val % 1 === 0 ? val.toFixed(0) : val.toFixed(1).replace(/\.0$/, '')) + 'M';
  }
  if (number >= 1_000) {
    const val = number / 1_000;
    return (val % 1 === 0 ? val.toFixed(0) : val.toFixed(1).replace(/\.0$/, '')) + 'K';
  }
  return number.toString();
}

/** Format goal for display: full number with commas below 10K, abbreviated (e.g. 10K) at 10K+. */
function formatGoalForDisplay(number) {
  if (typeof number !== 'number' || Number.isNaN(number)) {
    return '0';
  }
  const value = Number(number);
  if (value < GOAL_ABBREVIATE_THRESHOLD) {
    return addCommas(Math.round(value));
  }
  return abbreviateNumber(value);
}

function addCommas(number) {
  if (typeof number !== 'number') {
    throw new Error('Input must be a number.');
  }

  // Convert the number to a string
  let numberStr = number.toString();

  // Split the number into integer and decimal parts
  let [integerPart] = numberStr.split('.');

  // Add commas to the integer part
  integerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

  return integerPart;
}

/**
 * The goal track (FKO-0): a 6px pill on the sunken surface with the fill in the
 * brand accent role (SPEC F1), or the strong placeholder grey once the bar is
 * closed. The fill never drops below one dot (6px) when anything is raised
 * (SPEC E10), and never passes the track.
 */
const ProgressTrack = React.forwardRef(function ProgressTrack({ value, goal, closed = false, ...trackProps }, ref) {
  const ratio = goal > 0 ? Math.min(Math.max(value / goal, 0), 1) : 0;
  return (
    <div
      ref={ref}
      {...trackProps}
      className="cs-progress-track relative h-[var(--cs-size-progress)] w-full overflow-hidden rounded-[var(--cs-radius-full)] bg-[color:var(--cs-surface-sunken)]"
    >
      {value > 0 && (
        <div
          className={`absolute left-0 top-0 h-full min-w-[var(--cs-size-progress)] rounded-[var(--cs-radius-full)] transition-all duration-[var(--cs-motion-slower)] ease-out ${
            closed ? 'bg-[color:var(--cs-placeholder-strong)]' : 'bg-[color:var(--cs-brand-accent)]'
          }`}
          style={{ width: `${ratio * 100}%` }}
        />
      )}
    </div>
  );
});

function FundraisingBar({
  fundraisingBarID,
  brandColors: propBrandColors,
  amounts = {},
  previewMode = false,
  // CON-750: App.jsx's standalone root holds the skeleton until its own provider has decided
  // the currency, so the bar never paints USD and then flips after FINISHED_LOADING.
  waitForCurrency = false,
}) {
  const ctx = useDisplayCurrency();
  const moneyPrefix = moneyPrefixFor(ctx);
  const [hostedPage, setHostedPage] = useState(false);
  const [fetching, setLoading] = useState(true);
  const loading = fetching || (waitForCurrency && !ctx.ready);
  const [error, setError] = useState(null);
  const [active, setActive] = useState(false);
  const [elementID, setElementID] = useState('');
  const [elementName, setElementName] = useState('');
  const [goal, setGoal] = useState(10000000);
  const [raised, setRaised] = useState(500000);
  const [maxWidth, setMaxWidth] = useState('FULLSCREEN');
  const [raisedOffset, setRaisedOffset] = useState(0);
  const [trackingMetric, setTrackingMetric] = useState('DOLLAR_AMOUNT');
  const [subscriptionCount, setSubscriptionCount] = useState(0);
  const [productCount, setProductCount] = useState(0);
  const [donationCount, setDonationCount] = useState(0);
  const [metricLabel, setMetricLabel] = useState('Sustainers');
  const [elementOffset, setElementOffset] = useState(0);
  // A config that could not be loaded renders nothing (FKO-0 "No bar / load
  // fallback"): the old fallback drew a fake "$0 of $10M" bar on the host page.
  const [loadFailed, setLoadFailed] = useState(false);

  const [domID, setDomID] = useState('');

  // Configuration data state
  const [configData, setConfigData] = useState({});
  const [apiDataLoaded, setApiDataLoaded] = useState(false);

  // NEW: Use shared brand colors hook (SAME as StandardForm pattern)
  const { brandColors, primaryColor } = useBrandColors(
    configData?.color || configData?.lightColor || '#66b089'
  );

  const progressBarRef = useRef(null);
  // Use propBrandColors when available (preview mode OR when passed from parent), otherwise use generated brandColors
  const effectiveBrandColors = propBrandColors && Object.keys(propBrandColors).length > 0 ? propBrandColors : brandColors;
  const effectivePrimaryColor = effectiveBrandColors['accent-50'] || primaryColor;

  // Helper function to get text color - uses brand color if dark enough, otherwise uses darker shade
  const getTextColor = () => {
    const primaryColorToUse = effectiveBrandColors['accent-50'] || effectivePrimaryColor;

    // Check if the primary color is light (brightness > 70)
    // If light, use accent-60 (darker shade), otherwise use the brand color
    const hex = primaryColorToUse.replace(/^#/, '');
    const r = parseInt(hex.substring(0, 2), 16) / 255;
    const g = parseInt(hex.substring(2, 4), 16) / 255;
    const b = parseInt(hex.substring(4, 6), 16) / 255;
    const max = Math.max(r, g, b);
    const brightness = Math.round(max * 100);

    // If brightness > 70, the color is too light for white background, use darker shade
    return brightness > 70 ? (effectiveBrandColors['accent-60'] || primaryColorToUse) : primaryColorToUse;
  };

  const textColor = getTextColor();
  // The track and fill read the brand ROLE tokens (accent fill, SPEC F1). They
  // are written inline on this element's own root, never on the host document,
  // because the hosted page renders this component inside its own tree.
  const brandTokenStyle = useBrandTokenStyle({ palette: effectiveBrandColors });

  // Color generation now handled automatically by useBrandColors hook

  // create an async useEffect to fetch elements config
  const fetchElementsConfig = async () => {
    try {
      // If fundraisingBarID is 'NEW_FORM', skip API fetch and return early
      if (fundraisingBarID === 'NEW_FORM') {
        setActive(true);
        setElementName('Fundraising Bar (Preview)');
        setGoal(10000000);
        setRaised(0);
        setConfigData({ color: '#66b089' }); // Store fallback config
        setMaxWidth('FULLSCREEN');
        setRaisedOffset(0);
        setApiDataLoaded(true);
        setLoading(false); // Directly set loading to false in preview mode
        return;
      }

      // Check if we have the required backend URL
      const backendUrl = getBackendUrl();
      if (!backendUrl) {
        console.error('❌ FundraisingBar: VITE_ELEMENTS_BACKEND_URL is not configured!');
        console.error('❌ Current environment:', getEnvironment());
        console.error(
          '❌ Available env vars:',
          Object.keys(import.meta.env).filter((k) => k.startsWith('VITE_')),
        );
        throw new Error(
          'Backend URL environment variable is not set. Check VITE_ELEMENTS_BACKEND_URL configuration.',
        );
      }

      // Use the prop directly - elementID comes from fundraisingBarID prop
      let elementID = fundraisingBarID;

      // Skip API fetch if no valid fundraisingBarID (preview mode before form is saved)
      if (!elementID) {
        // In preview mode, show default values without API call
        setActive(true);
        setElementName('Fundraising Bar (Preview)');
        setGoal(10000000);
        setRaised(0);
        setConfigData({ lightColor: '#66b089' });
        setMaxWidth('FULLSCREEN');
        setRaisedOffset(0);
        setApiDataLoaded(true);
        setLoading(false);
        return;
      }

      setElementID(elementID);

      const apiUrl = `${backendUrl}/elements-config?elementID=${elementID}`;

      // CON-75: the hosted page keeps BOTH layout branches in the React tree
      // (the desktop rail is hidden with CSS, not unmounted), so this component
      // mounts twice and used to ask for the same payload twice, on the same
      // pipe, at the same moment. One request between them now.
      const response = await fetchElementsConfigOnce(apiUrl);

      if (!response.ok) {
        console.error('❌ FundraisingBar: API request failed');
        console.error('❌ Status:', response.status, response.statusText);
        console.error('❌ URL:', apiUrl);
        throw new Error(
          `API request failed with status ${response.status}: ${response.statusText}. URL: ${apiUrl}`,
        );
      }

      // The Response is shared with any concurrent mount, and a body can only
      // be read once — clone before reading.
      const data = await readSharedJson(response);
      // CON-750: a standalone bar's own provider is turned on by its config. Inside a form or
      // beside one (FollowDisplayCurrency) this is a no-op: the form's settings rule there.
      if (data?.currencySettings) ctx.applySettings(data.currencySettings);

      const parsed = {
        active: data.active,
        goal: data.goalAmount || data.goal || data.targetAmount || 10000000,
        raised: data.raised || data.amountOffset || 0,
        raisedOffset: data.raisedOffset === undefined ? 0 : data.raisedOffset,
        trackingMetric: data.trackingMetric || 'DOLLAR_AMOUNT',
        subscriptionCount: data.subscriptionCount || 0,
        productCount: data.productCount || 0,
        donationCount: data.donationCount || data.donations || 0,
        metricLabel: data.metricLabel || 'Sustainers',
        elementOffset: data.elementOffset || 0,
        color: data.color,
        size: data.size || 'FULLSCREEN',
        elementName: data.elementName || 'Fundraising Bar',
      };

      setActive(parsed.active);
      setGoal(parsed.goal);
      setRaised(parsed.raised);
      setConfigData(data);
      setElementName(parsed.elementName);
      setMaxWidth(parsed.size);
      setRaisedOffset(parsed.raisedOffset);
      setTrackingMetric(parsed.trackingMetric);
      setSubscriptionCount(parsed.subscriptionCount);
      setProductCount(parsed.productCount);
      setDonationCount(parsed.donationCount);
      setMetricLabel(parsed.metricLabel);
      setElementOffset(parsed.elementOffset);

      // Mark API data as loaded - actual loading state will be managed by useEffect
      // that waits for both API data AND brand colors to be ready
      setApiDataLoaded(true);
      setError(null);
    } catch (err) {
      console.error('❌ FundraisingBar: Error in fetchElementsConfig:', err);
      console.error('❌ Error details:', {
        message: err.message,
        stack: err.stack,
        elementID: elementID,
        backendUrl: getBackendUrl(),
        environment: getEnvironment(),
      });

      setActive(false);
      setLoadFailed(true);
      setApiDataLoaded(true);
      setLoading(false);
      setError(null);
    }
  };

  useEffect(() => {
    resizeIframe();

    if (loading === false && hostedPage !== null) {
      // send message that element has finished loading
      safePostMessageToParent(
        {
          action: 'FINISHED_LOADING',
          entity: 'CHARITYSTACK',
          sender: 'FUNDRAISING_BAR',
          receiver: 'HOSTED_PAGE',
        },
        '*',
      );
    }
  }, [loading]);

  useEffect(() => {
    // grab hostedPage boolean (only needs to run once)
    const urlParams = new URLSearchParams(window.location.search);
    const hostedPage = urlParams.get('page');
    // hostedPage: null (embedded) or truthy value (hosted page)
    setHostedPage(hostedPage);
    setDomID(urlParams.get('domID'));
  }, []);

  // Re-fetch when fundraisingBarID changes (only in non-preview mode)
  // In preview mode, data comes from amounts prop, not from API
  useEffect(() => {
    // Skip API fetch in preview mode - data comes from amounts prop
    if (previewMode) {
      setApiDataLoaded(true);
      setLoading(false);
      return;
    }

    setApiDataLoaded(false);
    setLoading(true);
    fetchElementsConfig().catch(() => {});
  }, [fundraisingBarID, previewMode]);

  useEffect(() => {
    if (previewMode && apiDataLoaded) {
      if (amounts?.raisedAmount !== undefined) {
        const raisedAmount =
          typeof amounts.raisedAmount === 'string'
            ? parseFloat(amounts.raisedAmount) || 0
            : amounts.raisedAmount;
        setRaised(raisedAmount);
      }

      if (amounts?.targetAmount !== undefined) {
        const targetAmount =
          typeof amounts.targetAmount === 'string'
            ? parseFloat(amounts.targetAmount) || 0
            : amounts.targetAmount;
        setGoal(targetAmount);
      }

      if (amounts?.trackingMetric) {
        setTrackingMetric(amounts.trackingMetric);
      }
      if (amounts?.subscriptionCount !== undefined) {
        setSubscriptionCount(amounts.subscriptionCount);
      }
      if (amounts?.productCount !== undefined) {
        setProductCount(amounts.productCount);
      }
      if (amounts?.donationCount !== undefined) {
        setDonationCount(amounts.donationCount);
      }
      if (amounts?.metricLabel) {
        setMetricLabel(amounts.metricLabel);
      }
      if (amounts?.elementOffset !== undefined) {
        setElementOffset(amounts.elementOffset);
      }

      setActive(true);
    }
  }, [previewMode, amounts, apiDataLoaded]);

  useEffect(() => {
    if (apiDataLoaded && Object.keys(brandColors).length > 0) {
      const hasValidColors = brandColors['accent-50'] && brandColors['accent-10'];
      if (hasValidColors) {
        setLoading(false);
      }
    }
  }, [apiDataLoaded, brandColors]);

  const resizeIframe = () => {
    const height = document.body.scrollHeight;
    const width = document.body.scrollWidth;

    safePostMessageToParent(
      {
        action: 'RESIZE',
        entity: 'CHARITYSTACK',
        sender: 'FUNDRAISING_BAR',
        receiver: 'HOSTED_SCRIPT',
        data: JSON.stringify({
          height: height,
          width: width,
          elementID: elementID,
          domID: domID,
          transitionDuration: 300,
          easing: 'ease-out',
          delay: 0,
        }),
      },
      '*',
    );
  };

  // CON-518 (Handoff map bug fix): a page with the bar switched on but no bar element used
  // to draw a "$0 raised out of $10M goal" placeholder (the goal's default). Outside the
  // dashboard preview, no element means no bar.
  if (!previewMode && !fundraisingBarID) {
    return null;
  }

  if (loading) {
    return (
      <NonFormSkeleton
        elementType="FUNDRAISING_BAR"
        brandColors={brandColors}
        primaryColor={configData?.color || configData?.lightColor || '#66b089'}
      />
    );
  }

  if (error) {
    return (
      <div className="mx-auto h-full max-w-lg items-center px-2 py-4">
        {/* FKO-0 / CON-517 status banner: error tint + border, 16 icon in the
            status colour, title 14/20 semibold ink. */}
        <div
          role="alert"
          className="flex items-center gap-[var(--cs-space-2)] rounded-[var(--cs-control-radius)] border border-[color:var(--cs-status-error-border)] bg-[color:var(--cs-status-error-bg)] px-[var(--cs-space-4)] py-[var(--cs-space-3)]"
        >
          <svg
            aria-hidden="true"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="var(--cs-icon-stroke)"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="size-[var(--cs-icon)] shrink-0 text-[color:var(--cs-status-error-text)]"
          >
            <circle cx="12" cy="12" r="10" />
            <path d="M12 8v4" />
            <path d="M12 16h.01" />
          </svg>
          <p className="text-[length:var(--cs-text-sm)] font-semibold leading-[var(--cs-leading-20)] text-[color:var(--cs-text-primary)]">
            Error loading fundraising bar
          </p>
        </div>
      </div>
    );
  }

  if (loadFailed) {
    return null;
  }

  if (!loading) {
    if (active) {
      const isSubscriptionMetric = trackingMetric === 'SUBSCRIPTION_COUNT';
      const isProductMetric = trackingMetric === 'PRODUCT_COUNT';
      const isDonationMetric = trackingMetric === 'DONATION_COUNT';
      const isCountMetric = isSubscriptionMetric || isProductMetric || isDonationMetric;
      const displayRaised = previewMode ? raised : raised + raisedOffset;
      const displaySubscriptionCount = previewMode ? subscriptionCount : subscriptionCount + elementOffset;
      const displayProductCount = previewMode ? productCount : productCount + elementOffset;
      const displayDonationCount = previewMode ? donationCount : donationCount + elementOffset;
      const displayValue = isSubscriptionMetric
        ? displaySubscriptionCount
        : isProductMetric
        ? displayProductCount
        : isDonationMetric
        ? displayDonationCount
        : displayRaised;

      // CON-518 ProgressStat (Paper "Progress stat"): a 6px pill on the sunken track, the
      // fill in the brand accent role (one dot wide at minimum when anything is counted,
      // full at or past the goal), then 8px below: the stat 20/28 semibold in the brand text
      // role, then the meta 14/20 secondary led by the percent in semibold: "$300  3% raised of
      // $10K goal", all left-aligned so it reads as one line and wraps as text (owner call
      // 2026-10-21; the percent used to sit alone at the right edge). Role tokens fall back to the palette for the
      // standalone element, which has no brand provider above it.
      const progressRatio = goal > 0 ? displayValue / goal : 0;
      const percent = Math.max(0, Math.round(progressRatio * 100));
      // A converted display currency shows the raised and goal figures in the donor's currency
      // (`moneyPrefix` is "€" there, no "≈" (owner ruling ui-a); "$" otherwise).
      const statValue = isCountMetric
        ? addCommas(displayValue)
        : `${moneyPrefix}${addCommas(displayTotal(displayRaised, ctx))}`;
      const goalLabel = isCountMetric
        ? `${formatGoalForDisplay(goal)} goal`
        : `${moneyPrefix}${formatGoalForDisplay(displayTotal(goal, ctx))} goal`;
      const percentLabel = `${percent}%`;
      // Money is "raised", counts are "committed" and name what they count:
      // "$300  3% raised of $10K goal" / "12  monthly donors · 24% committed of 50 goal".
      const statMetaLead = isCountMetric ? `${metricLabel} · ` : '';
      const statVerb = isCountMetric ? 'committed' : 'raised';
      const statMeta = `${statMetaLead}${percentLabel} ${statVerb} of ${goalLabel}`;

      return (
        <div data-name={elementName} style={brandTokenStyle}>
          <div className="mx-auto flex h-full w-full max-w-full flex-col gap-[var(--cs-space-2)]">
            {/* The same track as the Closed state (M4's ProgressTrack, FKO-0), announced as a
                progress bar with its stat as the label (CON-518). */}
            <ProgressTrack
              ref={progressBarRef}
              value={displayValue}
              goal={goal}
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={Math.min(percent, 100)}
              aria-label={`${statValue}, ${statMeta}`}
            />

            {/* `dynamic-bar-stats` stays the donor type ramp's hook. The raised stat no longer
                carries `dynamic-bar-raised`: the ramp and `.overlay-mode` stepped it down to
                11-14px in the embed card and the overlay, and SPEC holds it at 20/28 semibold at
                every width (CON-529 F5). */}
            <div className="dynamic-bar-stats flex items-baseline">
              <div className="flex min-w-0 flex-wrap items-baseline gap-x-[var(--cs-space-2)]">
                <span
                  className="text-[length:var(--cs-text-xl)] leading-[var(--cs-leading-28)] font-semibold"
                  style={{ color: `var(--cs-brand-text, ${textColor})` }}
                >
                  {statValue}
                </span>
                <span className="text-[length:var(--cs-text-sm)] leading-[var(--cs-leading-20)] text-[color:var(--cs-text-secondary)]">
                  {statMetaLead}
                  <span className="font-semibold">{percentLabel}</span> {statVerb} of {goalLabel}
                </span>
              </div>
            </div>
          </div>
        </div>
      );
    } else {
      // Inactive (admin turned the bar off). Hosted pages keep hiding it; an
      // embed keeps the final total visible but frozen — gray fill, gray stat
      // and a small "Closed" pill (FKO-0, approved Oct 9). The admin's element
      // name is never shown to donors.
      if (hostedPage !== null) {
        return null;
      }
      const isCountMetric = ['SUBSCRIPTION_COUNT', 'PRODUCT_COUNT', 'DONATION_COUNT'].includes(trackingMetric);
      const frozenCount =
        trackingMetric === 'SUBSCRIPTION_COUNT'
          ? subscriptionCount
          : trackingMetric === 'PRODUCT_COUNT'
            ? productCount
            : donationCount;
      const frozenValue = isCountMetric ? frozenCount + elementOffset : raised + raisedOffset;
      const frozenMoney = displayTotal(frozenValue, ctx);
      const frozenGoal = displayTotal(goal, ctx);
      return (
        <div data-name={elementName} data-cs-fundraising-bar-state="closed" className="mx-auto flex h-full max-w-full flex-col gap-[var(--cs-space-2)]">
          <ProgressTrack value={frozenValue} goal={goal} closed />
          <div className="flex flex-wrap items-center gap-[var(--cs-space-2)] [font-feature-settings:'calt'_0,'liga'_0]">
            <span className="text-[length:var(--cs-text-xl)] font-semibold leading-[var(--cs-leading-28)] text-[color:var(--cs-text-secondary)]">
              {isCountMetric ? `${addCommas(frozenValue)} ${metricLabel} committed` : `${moneyPrefix}${addCommas(frozenMoney)} raised`}
            </span>
            <span className="text-[length:var(--cs-text-sm)] leading-[var(--cs-leading-20)] text-[color:var(--cs-text-tertiary)]">
              {isCountMetric ? `of ${formatGoalForDisplay(goal)} goal` : `out of ${moneyPrefix}${formatGoalForDisplay(frozenGoal)} goal`}
            </span>
            <span className="rounded-[var(--cs-radius-full)] bg-[color:var(--cs-surface-sunken)] px-[var(--cs-space-2)] py-[var(--cs-space-hairline)] text-[length:var(--cs-text-xs)] font-medium leading-[var(--cs-leading-16)] text-[color:var(--cs-text-secondary)]">
              Closed
            </span>
          </div>
        </div>
      );
    }
  }
}

export default FundraisingBar;
